import React, { useEffect, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import styled from 'styled-components';

import { Table } from '@common/Table';
import { AppDispatch, State, flowActions } from '@store/index';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button } from '@common/Button';
import { Alert, Divider, IconButton, Skeleton, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { GridActionsCellItem, GridRenderCellParams } from '@mui/x-data-grid';
import { Service, getServices } from '@store/async/service';
import { useTheme } from '../../styles';
import { LayoutBase } from '@common/layout';
import { tabActions } from '@store/slices/tabs';
import { Card } from '@common/Card';
import { Tooltip } from '@common/Tooltip';
import { ResourceFallback } from '@common/ResourceFallback';
import { usePermissions } from '@common/hooks/usePermissions';
import { Resource } from '@store/types';

import {
  AutoAwesomeMotionOutlined,
  CheckOutlined,
  CloseOutlined,
  GridViewOutlined,
  TableViewOutlined,
} from '@mui/icons-material';

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: calc(100vw - 61px);
  height: calc(100vh - 70px - 28px);
  grid-gap: 1.5rem;
  padding: 28px;
  padding-top: 0;
  overflow-y: auto;
  align-content: flex-start;

  .card {
    align-content: flex-start;
    min-width: 200px;
    width: 360px;
  }
`;

const TableContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.line.light};
  margin: 28px;
  margin-top: 0;
  border-radius: 6px;
`;

const CardMetricContainer = styled.div`
  display: flex;
  flex-direction: column;

  p {
    font-size: 13px;
  }

  h1 {
    font-size: 20px;
  }
`;

const CardViewActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const WorkspaceActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  & .MuiToggleButton-root {
    padding: 4px 20px !important;
    min-height: 32px;
    max-height: 32px;
  }
`;

interface CardProps {
  title: string;
  metric: string | number | boolean;
}

const CardMetric: React.FC<CardProps> = ({ title, metric }) => {
  if (typeof metric === 'boolean') {
    return (
      <CardMetricContainer>
        <p>{title}</p>
        {metric ? <CheckOutlined /> : <CloseOutlined />}
      </CardMetricContainer>
    );
  }
  return (
    <CardMetricContainer>
      <p>{title}</p>
      <h1>{metric}</h1>
    </CardMetricContainer>
  );
};

export const Workspaces: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const theme = useTheme();

  const [view, setView] = useState<string | null>(localStorage.getItem('workspaceView') || 'cards');

  const data = useSelector((state: State) => state.flow.services);
  const createService = useSelector((state: State) => state.flow.createService);
  const isLoading = useSelector((state: State) => state.flow.flowLoading);
  const error = useSelector((state: State) => state.flow.flowError);
  const setOpen = (val: boolean) => dispatch(flowActions.setCreateService(val));

  const { isWorkspaceAdmin } = usePermissions(null, Resource.WORKSPACE);

  const loading = isLoading && !createService;

  useEffect(() => {
    // clear all tabs if the service page is loaded
    dispatch(tabActions.clear());
  }, []);

  const columns = [
    {
      field: 'service_name',
      headerName: 'Workspace',
      flex: 70,
      sortable: true,
    },
    {
      field: 'service_id',
      headerName: 'Domain',
      flex: 150,
      sortable: true,
      renderCell: (params: GridRenderCellParams<Service>) => {
        const row = params.row;

        return <pre>{`/execute/${row.service_id}/`}</pre>;
      },
    },
    {
      field: 'workflow_count',
      headerName: 'Workflows',
      flex: 50,
      sortable: true,
    },
    {
      field: 'bucket_count',
      headerName: 'Buckets',
      flex: 50,
      sortable: true,
    },
    {
      field: 'created_by',
      headerName: 'Created By',
      flex: 70,
      sortable: true,
    },
    {
      field: 'updated_by',
      headerName: 'Updated By',
      flex: 70,
      sortable: true,
    },
    {
      field: 'created_at',
      headerName: 'Created At (UTC)',
      flex: 100,
      sortable: true,
      renderCell: (params: GridRenderCellParams<Service>) => {
        const { row } = params;

        return <div>{new Date(row.updated_at).toLocaleString()}</div>;
      },
    },
    {
      field: 'updated_at',
      headerName: 'Updated At (UTC)',
      flex: 100,
      sortable: true,
      renderCell: (params: GridRenderCellParams<Service>) => {
        const { row } = params;

        return <div>{new Date(row.updated_at).toLocaleString()}</div>;
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      getActions: ({ id }: { id: string }) => {
        return [
          <GridActionsCellItem
            key='delete'
            icon={<DeleteIcon />}
            label='Delete'
            disabled={!isWorkspaceAdmin(id)}
            onClick={() => {
              dispatch(flowActions.setDeleteService(id));
            }}
            color='inherit'
          />,
        ];
      },
    },
  ];

  const tableView = (
    <TableContainer>
      <Table
        data={data}
        // page width - navbar - sidebar - sidebar padding
        width='calc(100vw - 60px - 56px)'
        height='calc(100vh - 60px - 56px)'
        columns={columns}
        density={'standard'}
        getRowId={(row) => row.service_id}
        loading={loading}
        onRowClick={async ({ row }) => {
          dispatch(tabActions.reset());
          navigate(`/${row.service_id}/workflow`);
        }}
      />
    </TableContainer>
  );

  const setWorkspaceView = (_: React.MouseEvent<HTMLElement>, newAlignment: string | null) => {
    if (!newAlignment) return;
    localStorage.setItem('workspaceView', newAlignment);
    setView(newAlignment);
  };

  const items = Array.from({ length: 10 }, (_, index) => index);

  const loadingCards = (
    <Container>
      {items.map((item) => (
        <Stack
          key={item}
          display='flex'
          justifyContent='space-between'
          alignItems='space-between'
          sx={{ height: 'calc(97px + 75px + 72px + 4px)' }}
        >
          <Skeleton variant='rounded' animation='wave' width={358} height={244} sx={{ borderRadius: '8px' }} />
        </Stack>
      ))}
    </Container>
  );

  const errorCard = (
    <Container>
      <Alert
        sx={{ width: '100%' }}
        severity='error'
        action={
          <Button small customColor='red' onClick={() => dispatch(getServices())}>
            Retry
          </Button>
        }
      >
        Unable to load workspaces: {error}
      </Alert>
    </Container>
  );

  const cardView = (
    <Container>
      {data?.length === 0 && (
        <ResourceFallback
          resourceName='Workspace'
          resourceIcon={<AutoAwesomeMotionOutlined />}
          resourceInfo='A workspace is a collection of workflows and their associated configurations. You can use workspaces to
          categorise your projects and couple related workflows.'
          onClickCreate={() => setOpen(true)}
          onClickGuide={() => navigate(`/documentation/introduction/getting_started`)}
          containerHeight='calc(100vh - 70px - 28px)'
          containerWidth='calc(100vw - 61px)'
        />
      )}
      {data.map((workspace) => {
        const admin = isWorkspaceAdmin(workspace.service_id);

        return (
          <div className='card' key={workspace.service_id}>
            <Card
              title={workspace.service_name}
              chip={admin ? 'Admin' : undefined}
              icon={<AutoAwesomeMotionOutlined sx={{ color: theme.primary, fontSize: '2ch' }} />}
              description={`/execute/${workspace.service_id}/`}
              descriptionBorder
              body={
                <Stack divider={<Divider orientation='vertical' flexItem />} display='flex' direction='row' spacing={3}>
                  <CardMetric title='Workflows' metric={workspace.workflow_count} />
                  <CardMetric title='Buckets' metric={workspace.bucket_count} />
                  {/* <CardMetric title='Secrets' metric={workspace.secret_count > 0} /> */}
                </Stack>
              }
              actions={
                <CardViewActions>
                  <Button
                    customColor='primary'
                    onClick={() => {
                      dispatch(tabActions.reset());
                      navigate(`/${workspace.service_id}/workflow`);
                    }}
                  >
                    Open
                  </Button>
                  <Stack direction='row' spacing='8px'>
                    {/* <IconButton disabled>
                    <Edit />
                  </IconButton> */}
                    <IconButton
                      onClick={() => dispatch(flowActions.setDeleteService(workspace.service_id))}
                      disabled={!admin}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                </CardViewActions>
              }
            />
          </div>
        );
      })}
    </Container>
  );

  return (
    <LayoutBase
      header='Workspaces'
      tooltip={
        data.length > 0
          ? 'A workspace is a collection of workflows and their associated configurations. You can use workspaces to categorise your projects and couple related workflows.'
          : undefined
      }
      actions={
        <WorkspaceActions>
          {/*
            Disabled since this is no longer a new feature, but can be reused in future if we add something big 
            <ExecutionFeatureVideo /> 
          */}
          {data.length > 0 && (
            <ToggleButtonGroup
              size='small'
              color='primary'
              value={view}
              exclusive
              onChange={setWorkspaceView}
              aria-label='text alignment'
            >
              <Tooltip title='Card View'>
                <ToggleButton value='cards' aria-label='cards'>
                  <GridViewOutlined sx={{ fontSize: '2.2ch' }} />
                </ToggleButton>
              </Tooltip>
              <Tooltip title='Table View'>
                <ToggleButton value='table' aria-label='table'>
                  <TableViewOutlined sx={{ fontSize: '2.2ch' }} />
                </ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
          )}
          {data.length > 0 && (
            <Button data-intercom-target='workspace-create-button' customColor='black' onClick={() => setOpen(true)}>
              Create Workspace
            </Button>
          )}
        </WorkspaceActions>
      }
      body={error ? errorCard : view === 'cards' ? (loading ? loadingCards : cardView) : tableView}
    />
  );
};
